import React, { useState } from 'react';
import { PatientFileType } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface UploadFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileTypes: PatientFileType[];
    onUpload: (filename: string, fileTypeId: number, base64file: string, comments: string) => Promise<void>;
    isUploading?: boolean;
}

export const UploadFileModal: React.FC<UploadFileModalProps> = ({
    isOpen,
    onClose,
    fileTypes,
    onUpload,
    isUploading = false,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFileTypeId, setSelectedFileTypeId] = useState<number>(0);
    const [comments, setComments] = useState('');
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setError(null);
        }
    };

    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file');
            return;
        }

        if (!selectedFileTypeId) {
            setError('Please select a file type');
            return;
        }

        try {
            setError(null);
            const base64file = await convertToBase64(selectedFile);
            await onUpload(selectedFile.name, selectedFileTypeId, base64file, comments);
            // Reset form
            setSelectedFile(null);
            setSelectedFileTypeId(0);
            setComments('');
            // onClose will be called by parent after successful upload
        } catch (err: any) {
            setError(err.message || 'Failed to upload file');
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setSelectedFileTypeId(0);
        setComments('');
        setError(null);
        onClose();
    };

    return (
        <ModalWrapper modalId="UploadFileModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Upload"
            isLoading={isUploading}
        >
            {/* Description Input */}
            <div className="mb-4">
                <input
                    type="text"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className={POPUP_STYLES.input}
                    placeholder="Description"
                    disabled={isUploading}
                />
            </div>

            {/* File Type Dropdown */}
            <div className="mb-4">
                <div className="relative">
                    <select
                        value={selectedFileTypeId}
                        onChange={(e) => setSelectedFileTypeId(Number(e.target.value))}
                        className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none pr-10 text-[#366A75]"
                        disabled={isUploading}
                    >
                        <option value={0}>File Type</option>
                        {fileTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.description}
                            </option>
                        ))}
                    </select>
                    {/* Dropdown arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* File Input */}
            <div className="mb-6">
                <div className="relative">
                    <input
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        disabled={isUploading}
                    />
                    <label
                        htmlFor="file-upload"
                        className="w-full px-4 py-2 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] flex items-center justify-between cursor-pointer hover:bg-[#D0E7E5]"
                    >
                        <span className="text-[#366A75] text-sm">
                            {selectedFile ? selectedFile.name : 'No file selected'}
                        </span>
                        <svg className="w-5 h-5 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                    </label>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Upload Button */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className={POPUP_STYLES.button}
                >
                    {isUploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isUploading}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
